import React, { Fragment, useEffect, useState } from 'react';
import { FolderUp } from 'lucide-react';
import './CustomerComplaintForm.css';
import PageWrap from '../PageWrap/PageWrap';
import { validateFileSize } from '../../../helpers/validateFileSize';
import { toast } from 'react-toastify';
import { useUploadFrontViewImageMutation } from '../../../Actions/BulkUpload/BulkUpload';
import MoonLoader from 'react-spinners/MoonLoader';
import { useCreateComplaintMutation } from '../../../Actions/Complaint/Complaint';



const override = {
  margin: "0",
  borderColor: "none",
  height: "50px",
  width: "100%",
  alignItems: "center",
  justifyContent: "center"
};

export default function CustomerComplaintForm() {
  const [formData, setFormData] = useState({
    userType: '',
    fullName: '',
    email: '',
    phone: '',
    orderOrTrxnId: '',
    purchaseOrTrxnDate: '',
    productOrServiceName: '',
    complaintCategory: '',
    preferredResolution: '',
    description: '',
    confirmAccuracy: false,
    consentContact: false,
  });

  const [showOtherCategory, setShowOtherCategory] = useState(false);
  const [showOtherResolution, setShowOtherResolution] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

   const [uploadImage, { data, isLoading: isLoadingFile, reset }] = useUploadFrontViewImageMutation();
   const [createComplaint, {isLoading}] = useCreateComplaintMutation()

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setShowOtherCategory(value === 'Other');
    setFormData((prev) => ({
      ...prev,
      complaintCategory: value === 'Other' ? '' : value,
    }));
  };

  const handleResolutionChange = (e) => {
    const value = e.target.value;
    setShowOtherResolution(value === 'other');
    setFormData((prev) => ({
      ...prev,
      preferredResolution: value === 'other' ? '' : value,
    }));
  };



  const handleFileChange = async (e, uploadFunc, type) => {
  const file = e.target.files[0];
  if (!file) return;

  const error = await validateFileSize(file, type);
  if (error) {
    toast.error(error);
    return;
  }

  try {
    await uploadFunc(file).unwrap();
    setUploadedFile(file);
  } catch (error) {
    const message =
      error?.response?.data || error?.data?.message || error?.message || "File upload failed.";
    toast.error(message);
  }
};

const handleReset = () => {
     setUploadedFile(null);
     reset()
}

const {
    userType,
    fullName,
    email,
    phone,
    orderOrTrxnId,
    purchaseOrTrxnDate,
    productOrServiceName,
    complaintCategory,
    preferredResolution,
    description,
    confirmAccuracy,
    consentContact,
  } = formData;


 const handleSubmit = async (e) => {
  e.preventDefault();

  if (
    !userType ||
    !fullName ||
    !email ||
    !phone ||
    !orderOrTrxnId ||
    !complaintCategory ||
    !preferredResolution ||
    !description
  ) {
    toast.warning("Please provide all required information");
    return;
  }

  if (!confirmAccuracy) {
    toast.warning("Please confirm the accuracy of the information.");
    return;
  }

  if (!consentContact) {
    toast.warning("Please consent to being contacted for further details.");
    return;
  }

  const complaintData = {
    userType,
    fullName,
    email,
    phone,
    orderOrTrxnId,
    purchaseOrTrxnDate,
    productOrServiceName,
    complaintCategory,
    preferredResolution,
    description,
    attachment: data?.url || null,
  };

  try {
    await createComplaint(complaintData).unwrap();
    toast.success("Complaint submitted successfully.");

    setFormData({
      userType: '',
      fullName: '',
      email: '',
      phone: '',
      orderOrTrxnId: '',
      purchaseOrTrxnDate: '',
      productOrServiceName: '',
      complaintCategory: '',
      preferredResolution: '',
      description: '',
      confirmAccuracy: false,
      consentContact: false,
    });

    setUploadedFile(null);
    reset();
    setShowOtherCategory(false);
    setShowOtherResolution(false);

  } catch (error) {
    const message =
      error?.response?.data || error?.data?.message || error?.message || "Submission failed.";
    toast.error(message);
  }
};


 const today = new Date().toISOString().split('T')[0]


  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <PageWrap>
      <div className="complaint-container">
        <form className="complaint-card" onSubmit={handleSubmit}>
          <h1 className="form-title">Customer Complaint Form</h1>
          <p className="form-subtitle">Please fill out the form below to submit your complaint</p>

          {/* User Type */}
          <div className="form-group">
            <label className="user__type">User Type <span className="required">*</span></label>
            <div className="radio-group">
              <label className="user__type-label">
                <input
                  type="radio"
                  name="userType"
                  value="buyer"
                  checked={formData.userType === 'buyer'}
                  onChange={handleInputChange}
                  required
                />
                Buyer
              </label>
              <label className="user__type-label">
                <input
                  type="radio"
                  name="userType"
                  value="merchant"
                  checked={formData.userType === 'merchant'}
                  onChange={handleInputChange}
                  required
                />
                Merchant
              </label>
            </div>
          </div>

          {/* Two Column Grid */}
          <div className="two-column">
            <div className="form-group">
              <label>Full Name <span className="required">*</span></label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="two-column">
            <div className="form-group">
              <label>Phone Number <span className="required">*</span></label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Order ID / Transaction Number <span className="required">*</span></label>
              <input
                type="text"
                name="orderOrTrxnId"
                value={formData.orderOrTrxnId}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="two-column">
            <div className="form-group">
              <label>Purchase/Transaction Date</label>
              <input
                type="date"
                name="purchaseOrTrxnDate"
                value={formData.purchaseOrTrxnDate}
                onChange={handleInputChange}
                max={today}
              />
            </div>
            <div className="form-group">
              <label>Product/Service Name</label>
              <input
                type="text"
                name="productOrServiceName"
                value={formData.productOrServiceName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="two-column">
            {/* Complaint Category */}
            <div className="form-group">
              <label>Complaint Category <span className="required">*</span></label>
              <select
                name="complaintCategory"
                value={showOtherCategory ? 'Other' : formData.complaintCategory}
                onChange={handleCategoryChange}
                required
              >
                <option value="">Select category</option>
                <option value="Item Not Delivered">Item Not Delivered</option>
                <option value="Wrong Item Received">Wrong Item Received</option>
                <option value="Item Damaged or Defective">Item Damaged or Defective</option>
                <option value="Payment Issue">Payment Issue</option>
                <option value="Return/Refund Problem">Return/Refund Problem</option>
                <option value="Seller/Merchant Conduct">Seller/Merchant Conduct</option>
                <option value="Buyer Misconduct">Buyer Misconduct</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Preferred Resolution */}
            <div className="form-group">
              <label>Preferred Resolution <span className="required">*</span></label>
              <select
                name="preferredResolution"
                value={showOtherResolution ? 'other' : formData.preferredResolution}
                onChange={handleResolutionChange}
                required
              >
                <option value="">Select resolution</option>
                <option value="fullRefund">Full Refund</option>
                <option value="replacement">Replacement</option>
                <option value="partial Refund">Partial Refund</option>
                <option value="apology">Apology</option>
                <option value="report User">Report the User</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {showOtherCategory && (
            <div className="form-group">
              <label>Please specify other category</label>
              <input
                type="text"
                name="complaintCategory"
                value={formData.complaintCategory}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          {showOtherResolution && (
            <div className="form-group">
              <label>Please specify other resolution</label>
              <input
                type="text"
                name="preferredResolution"
                value={formData.preferredResolution}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Detailed Description <span className="required">*</span></label>
            <textarea
              className="complaint__message"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Upload Section */}
          <div className="form-group">
            <label className="upload-box">
                {
                    isLoadingFile ? (
                        <div className="loader-container">
                        <MoonLoader size={40} color={"#000000"} loading={isLoadingFile} cssOverride={override} />
                        </div>
                    ) : 
                   <Fragment>
                        {
                            (!uploadedFile  || !data?.url) &&(
                                <Fragment>
                                <FolderUp size={32} className="upload-icon" />
                                <span className="upload-label">Click to upload file (Optional)</span>
                                </Fragment>
                            )
                        }
            
                    <input
                        type="file"
                        onChange={(e) => handleFileChange(e, uploadImage, 'image')}
                        accept="image/*,.pdf,.doc,.docx"
                        className="upload-input"
                    />
                    {uploadedFile && data?.url && (
                        <p className="upload-success">File uploaded: {uploadedFile.name}</p>
                    )}
                    {uploadedFile && data?.url && (
                        <button className="btn btn-secondary" onClick={handleReset}>Change file</button>
                    )}
                 </Fragment>
              }
            </label>
          </div>

          {/* Checkboxes */}
          <div className="form-group checkbox-group">
            <label className="accuracy-consent">
              <input
                type="checkbox"
                name="confirmAccuracy"
                checked={formData.confirmAccuracy}
                onChange={handleInputChange}
                required
              />
              I confirm that the information provided is accurate. <span className="required">*</span>
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label className="accuracy-consent">
              <input
                type="checkbox"
                name="consentContact"
                checked={formData.consentContact}
                onChange={handleInputChange}
                required
              />
              I consent to being contacted for further details. <span className="required">*</span>
            </label>
          </div>

          <button disabled={isLoading} type="submit" className="submit-button">{ isLoading ?    "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </PageWrap>
  );
}
